import React, { useState, useEffect, useRef } from 'react'
import styled from '@emotion/styled'
import { useLocationAutocomplete } from 'src/hooks/useLocationAutocomplete'
import { operacionOptions, propiedadOptions, SHOW_IA_SEARCH } from './classicSearch.constants'

export type ViewMode = 0 | 1 | 2 // 0=lista, 1=mapa completo, 2=lista+mapa

export interface SearchFilterBarProps {
	onOpenFilters: () => void
	viewMode: ViewMode
	onViewModeChange: (mode: ViewMode) => void
	tipoOperacionFamilia?: string
	tipoPropiedadFamilia?: string
	onOperacionChange?: (value: string) => void
	onPropiedadChange?: (value: string) => void
	/** Modo compacto: oculta toggle de vistas y botón guardar (para uso en modales) */
	compact?: boolean
	isMobile?: boolean
	onSave?: () => void
	/** true mientras se muestra la animación de guardado (el botón "se invierte") */
	saved?: boolean
}

/* ─── SVG Icons ─────────────────────────────────────── */

const MagnifyIcon = () => (
	<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
		<circle cx="11" cy="11" r="7" />
		<path d="m21 21-4.35-4.35" />
	</svg>
)

const PinIcon = () => (
	<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0, color: '#999' }}>
		<path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" /><circle cx="12" cy="10" r="3" />
	</svg>
)

const FilterIcon = () => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
	</svg>
)

const BookmarkIcon = () => (
	<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
	</svg>
)

const CheckIcon = () => (
	<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
		<polyline points="20 6 9 17 4 12" />
	</svg>
)

const SparkleIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
		<path d="M20 3v4" /><path d="M22 5h-4" />
		<path d="M4 17v2" /><path d="M5 18H3" />
	</svg>
)

const IconListView = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<path d="M3 5h.01" /><path d="M3 12h.01" /><path d="M3 19h.01" />
		<path d="M8 5h13" /><path d="M8 12h13" /><path d="M8 19h13" />
	</svg>
)

const IconMapView = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<path d="M14.106 5.553a2 2 0 0 0 1.788 0l3.659-1.83A1 1 0 0 1 21 4.619v12.764a1 1 0 0 1-.553.894l-4.553 2.277a2 2 0 0 1-1.788 0l-4.212-2.106a2 2 0 0 0-1.788 0l-3.659 1.83A1 1 0 0 1 3 19.381V6.618a1 1 0 0 1 .553-.894l4.553-2.277a2 2 0 0 1 1.788 0z" />
		<path d="M15 5.764v15" /><path d="M9 3.236v15" />
	</svg>
)

const IconSplitView = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<rect width="18" height="18" x="3" y="3" rx="2" />
		<path d="M12 3v18" />
	</svg>
)

/* ─── Component ─────────────────────────────────────── */

const defaultSearchMode = process.env.NEXT_PUBLIC_DEFAULT_SEARCH_MODE === 'normal' ? 1 : 0

export const SearchFilterBar = ({
	onOpenFilters,
	viewMode,
	onViewModeChange,
	tipoOperacionFamilia = '',
	tipoPropiedadFamilia = '',
	onOperacionChange,
	onPropiedadChange,
	compact = false,
	isMobile = false,
	onSave,
	saved = false,
}: SearchFilterBarProps) => {
	// Búsqueda IA oculta este release (SHOW_IA_SEARCH) — fuerza modo clásico sin importar defaultSearchMode.
	const [searchMode, setSearchMode] = useState<0 | 1>(SHOW_IA_SEARCH ? (defaultSearchMode as 0 | 1) : 1)
	const [selectedOperacion, setSelectedOperacion] = useState(
		() => operacionOptions.find(o => o.value === tipoOperacionFamilia)?.value ?? operacionOptions[0].value
	)
	const [selectedPropiedad, setSelectedPropiedad] = useState(
		() => propiedadOptions.find(p => p.value === tipoPropiedadFamilia)?.value ?? propiedadOptions[0].value
	)

	// IA search state
	const [texto, setTexto] = useState('')
	const [iaError, setIaError] = useState<string | null>(null)
	const [iaCargando, setIaCargando] = useState(false)

	// Ubicación autocomplete state (modo Clásico)
	const ubic = useLocationAutocomplete()
	const [ubicError, setUbicError] = useState<string | null>(null)
	const searchAreaRef = useRef<HTMLDivElement>(null)

	const handleOperacionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setSelectedOperacion(e.target.value)
		onOperacionChange?.(e.target.value)
	}

	const handlePropiedadChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setSelectedPropiedad(e.target.value)
		onPropiedadChange?.(e.target.value)
	}

	// Precarga los inputs con el resultado guardado por el buscador anterior (home, otra lista, etc.)
	useEffect(() => {
		try {
			const rawAddress = localStorage.getItem('toctocSearchAddress')
			const savedAddress = rawAddress ? JSON.parse(rawAddress) : null
			if (savedAddress?.label) ubic.restore(savedAddress)

			const rawIa = localStorage.getItem('toctocSearchIa')
			const iaLabel = rawIa ? JSON.parse(rawIa)?.label : null
			if (iaLabel) setTexto(iaLabel)
		} catch {
			// localStorage no disponible o con contenido corrupto: se ignora
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (!iaError) return
		const handler = () => setIaError(null)
		document.addEventListener('mousedown', handler)
		return () => document.removeEventListener('mousedown', handler)
	}, [iaError])

	useEffect(() => {
		if (ubic.suggestions.length === 0 && !ubicError) return
		const handler = (e: MouseEvent) => {
			if (searchAreaRef.current?.contains(e.target as Node)) return
			ubic.dismissSuggestions()
			setUbicError(null)
		}
		document.addEventListener('mousedown', handler)
		return () => document.removeEventListener('mousedown', handler)
	}, [ubic.suggestions.length, ubicError])

	const handleBuscarClasico = () => {
		const { url, error } = ubic.buildUrl({ operacion: selectedOperacion, tipoPropiedad: selectedPropiedad })
		if (error) {
			setUbicError(error)
			return
		}
		window.location.href = url as string
	}

	const handleBuscarIA = async (e: React.FormEvent | React.MouseEvent) => {
		e.preventDefault()
		if (!texto.trim() || iaCargando) return

		setIaCargando(true)
		setIaError(null)
		const inicioMs = Date.now()

		const finCargando = (fn: () => void) => {
			const restante = Math.max(0, 650 - (Date.now() - inicioMs))
			setTimeout(fn, restante)
		}

		try {
			const baseApi = process.env.NEXT_PUBLIC_HOME_BASE_API
			const tokenRes = await fetch(`${baseApi}/search/ia-token`)
			const { token } = await tokenRes.json()

			const res = await fetch(`${baseApi}/search/ia`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ texto, token }),
			})
			const data = await res.json()

			if (data.exito && data.url) {
				try {
					localStorage.setItem('toctocSearchIa', JSON.stringify(data))
				} catch {
					// localStorage no disponible: se ignora
				}
				window.location.href = data.url
			} else {
				finCargando(() => {
					setIaError(data.message || 'Error al procesar la búsqueda.')
					setIaCargando(false)
				})
			}
		} catch {
			finCargando(() => {
				setIaError('Error al conectar con el servicio de búsqueda.')
				setIaCargando(false)
			})
		}
	}

	return (
		<BarContainer data-apartado={`${tipoOperacionFamilia} ${tipoPropiedadFamilia}`}>
			{/* Tabs Clásico / Búsqueda IA — oculto este release (SHOW_IA_SEARCH en classicSearch.constants) */}
			{SHOW_IA_SEARCH && (
				<SearchModeTabs>
					<ModeTab data-active={searchMode === 1 ? "true" : undefined} onClick={() => setSearchMode(1)}>
						Clásico
					</ModeTab>
					<ModeTab data-active={searchMode === 0 ? "true" : undefined} onClick={() => setSearchMode(0)}>
						<SparkleIcon /> Búsqueda IA
					</ModeTab>
				</SearchModeTabs>
			)}

			{/* Dropdowns Clásico — visibles solo en modo Clásico */}
			{searchMode === 1 && (
				<>
					<FilterSelect value={selectedOperacion} onChange={handleOperacionChange} aria-label="Tipo de operación">
						{operacionOptions.map(o => (
							<option key={o.value} value={o.value}>{o.label}</option>
						))}
					</FilterSelect>
					<FilterSelect value={selectedPropiedad} onChange={handlePropiedadChange} aria-label="Tipo de propiedad">
						{propiedadOptions.map(p => (
							<option key={p.value} value={p.value}>{p.label}</option>
						))}
					</FilterSelect>
				</>
			)}

			{/* Área de búsqueda: input + botón buscar */}
			<SearchArea ref={searchAreaRef}>
				<SearchForm onSubmit={handleBuscarIA}>
					<InputWrapper data-loading={searchMode === 0 && iaCargando ? "true" : undefined}>
						{searchMode === 0 && iaCargando ? (
							<LoadingRow>
								<MagnifyIcon />
								<LoadingLabel>Buscando propiedades compatibles...</LoadingLabel>
								<LoadingQuery>&ldquo;{texto}&rdquo;</LoadingQuery>
							</LoadingRow>
						) : (
							<>
								{searchMode === 0 && (
									<InputSparkle>
										<SparkleIcon />
									</InputSparkle>
								)}
								<SearchInput
									type="text"
									placeholder={searchMode === 0 ? 'Ej: "Depto cerca de metro en Providencia"' : 'Comuna o ciudad'}
									value={searchMode === 0 ? texto : ubic.query}
									onChange={(e) => {
										if (searchMode === 0) setTexto(e.target.value)
										else { setUbicError(null); ubic.setQuery(e.target.value) }
									}}
									autoComplete="off"
								/>
							</>
						)}
					</InputWrapper>

					{!(searchMode === 0 && iaCargando) && (
						<BuscarButton
							type={searchMode === 0 ? 'submit' : 'button'}
							onClick={searchMode === 1 ? handleBuscarClasico : undefined}
							disabled={searchMode === 0 && !texto.trim()}
							data-event="click"
							data-event-key="buscar-ia"
							data-location="lista-seo"
						>
							<MagnifyIcon />
							Buscar
						</BuscarButton>
					)}
				</SearchForm>

				{/* Sugerencias de ubicación — modo Clásico */}
				{searchMode === 1 && ubic.suggestions.length > 0 && (
					<SuggestionsDropdown role="listbox">
						{ubic.suggestions.map((s, i) => (
							<SuggestionRow key={`${s.label}-${i}`} role="option" onClick={() => ubic.select(s)}>
								<PinIcon />
								<SuggestionText>
									<strong>{s.label}</strong>
									{s.sublabel && <span>{s.sublabel}</span>}
								</SuggestionText>
							</SuggestionRow>
						))}
					</SuggestionsDropdown>
				)}

				{/* Error inline */}
				{iaError && !iaCargando && (
					<ErrorMessage>
						<p>{iaError}</p>
					</ErrorMessage>
				)}
				{searchMode === 1 && ubicError && ubic.suggestions.length === 0 && (
					<ErrorMessage>
						<p>{ubicError}</p>
					</ErrorMessage>
				)}
			</SearchArea>

			{/* Más filtros */}
			{!compact && (
				<FilterButton type="button" onClick={onOpenFilters}>
					<FilterIcon />
					Más filtros
				</FilterButton>
			)}

			{/* Toggle 3 modos de vista */}
			{!compact && (
				<ViewToggle>
					<ViewBtn
						data-active={viewMode === 0 ? "true" : undefined}
						onClick={() => onViewModeChange(0)}
						title="Lista"
						data-elemento="lista"
					>
						<IconListView />
					</ViewBtn>
					<ViewBtn
						data-active={viewMode === 1 ? "true" : undefined}
						onClick={() => onViewModeChange(1)}
						title="Mapa completo"
						data-elemento="mapa"
					>
						<IconMapView />
					</ViewBtn>
					{/* PRUEBA: vista "Lista y mapa" deshabilitada en mobile — solo visible en desktop.
					    Para revertir, quitar la condición `!isMobile &&`. */}
					{!isMobile && (
						<ViewBtn
							data-active={viewMode === 2 ? "true" : undefined}
							onClick={() => onViewModeChange(2)}
							title="Lista y mapa"
							data-elemento="lista-mapa"
						>
							<IconSplitView />
						</ViewBtn>
					)}
				</ViewToggle>
			)}

			{/* Guardar */}
			{!compact && (
				<SaveButton
					type="button"
					onClick={onSave}
					disabled={!onSave || saved}
					data-status={saved ? 'saved' : 'idle'}
					aria-label={saved ? 'Búsqueda guardada' : 'Guardar búsqueda'}
					data-event="click"
					data-event-key="guardar-busqueda"
					data-location="lista-seo"
				>
					<SaveButtonInner className="save-inner">
						<SaveButtonFace className="face-front">
							<BookmarkIcon />
							Guardar
						</SaveButtonFace>
						<SaveButtonFace className="face-back">
							<CheckIcon />
							Guardado
						</SaveButtonFace>
					</SaveButtonInner>
				</SaveButton>
			)}
		</BarContainer>
	)
}

/* ─── Styled Components ─────────────────────────────────────── */

const BarContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	background: #fff;
	padding: 10px 0;
	flex-wrap: wrap;

	@media (max-width: 768px) {
		flex-direction: column;
		align-items: stretch;
	}
`

const SearchModeTabs = styled.div`
	display: flex;
	align-items: center;
	border: 1px solid #E0E0E0;
	border-radius: 8px;
	overflow: hidden;
	flex-shrink: 0;
`

const ModeTab = styled.button`
	display: flex;
	align-items: center;
	gap: 5px;
	background: transparent;
	color: #666;
	border: none;
	padding: 8px 14px;
	font-family: nunito, sans-serif;
	font-size: 13px;
	font-weight: 400;
	cursor: pointer;
	white-space: nowrap;
	transition: background 0.15s, color 0.15s;

	&[data-active="true"] {
		background: #3200C1;
		color: #fff;
		font-weight: 700;
	}
`

const SearchArea = styled.div`
	display: flex;
	flex: 1;
	align-items: center;
	gap: 8px;
	position: relative;
	min-width: 0;

	@media (max-width: 768px) {
		flex-direction: column;
		align-items: stretch;
	}
`

const SearchForm = styled.form`
	display: flex;
	flex: 1;
	align-items: center;
	gap: 8px;
	min-width: 0;

	@media (max-width: 768px) {
		flex-direction: column;
		align-items: stretch;
	}
`

const InputWrapper = styled.div`
	display: flex;
	align-items: center;
	flex: 1;
	border: 1px solid #E0E0E0;
	border-radius: 8px;
	background: #fff;
	padding: 0 10px;
	min-width: 160px;
	gap: 8px;
	transition: border-color 0.2s, background 0.2s;

	&[data-loading="true"] {
		border-color: #C1B2EC;
		background: #F3F0FE;
	}
`

const InputSparkle = styled.span`
	display: flex;
	align-items: center;
	color: #6c5ce7;
	flex-shrink: 0;
`

const SearchInput = styled.input`
	flex: 1;
	border: none;
	background: transparent;
	padding: 9px 0;
	font-family: nunito, sans-serif;
	font-size: 14px;
	color: #333;
	outline: none;

	&::placeholder {
		color: #B1B1B1;
	}
`

const BuscarButton = styled.button`
	display: flex;
	align-items: center;
	gap: 6px;
	background: rgb(55, 255, 219);
	color: #1a1a2e;
	border: none;
	border-radius: 8px;
	padding: 9px 18px;
	font-family: nunito, sans-serif;
	font-size: 14px;
	font-weight: 700;
	cursor: pointer;
	white-space: nowrap;
	flex-shrink: 0;

	&:hover:not(:disabled) {
		background: rgb(30, 240, 200);
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`

const LoadingRow = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 9px 0;
	min-width: 0;
	color: #3200C1;
`

const LoadingLabel = styled.span`
	font-family: nunito, sans-serif;
	font-size: 14px;
	font-weight: 700;
	color: #3200C1;
	white-space: nowrap;
	flex-shrink: 0;
`

const LoadingQuery = styled.span`
	font-family: nunito, sans-serif;
	font-size: 14px;
	color: #8A8A8A;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`

const ErrorMessage = styled.div`
	position: absolute;
	top: calc(100% + 8px);
	left: 0;
	right: 0;
	background: #FFF3F4;
	border: 1px solid #E63946;
	border-radius: 8px;
	padding: 10px 14px;
	display: flex;
	align-items: flex-start;
	gap: 8px;
	z-index: 5;

	&::before {
		content: "⚠";
		color: #E63946;
		font-size: 16px;
		flex-shrink: 0;
	}

	p {
		margin: 0;
		color: #333;
		font-family: nunito, sans-serif;
		font-size: 14px;
		line-height: 1.4;
	}
`

const SuggestionsDropdown = styled.ul`
	position: absolute;
	top: calc(100% + 4px);
	left: 0;
	right: 0;
	margin: 0;
	padding: 6px 0;
	background: #fff;
	border: 1px solid #E0E0E0;
	border-radius: 8px;
	box-shadow: 0 4px 16px rgba(0,0,0,0.10);
	z-index: 10;
	list-style: none;
	overflow: hidden;
`

const SuggestionRow = styled.li`
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 10px 14px;
	font-family: nunito, sans-serif;
	font-size: 14px;
	color: #333;
	cursor: pointer;

	&:hover { background: #f5f5f5; }
`

const SuggestionText = styled.span`
	display: flex;
	flex-direction: column;
	gap: 1px;
	min-width: 0;

	strong {
		font-weight: 700;
	}

	span {
		font-size: 12px;
		color: #888;
	}
`

const FilterButton = styled.button`
	display: flex;
	align-items: center;
	gap: 6px;
	background: transparent;
	color: #333;
	border: 1px solid #E0E0E0;
	border-radius: 8px;
	padding: 8px 14px;
	font-family: nunito, sans-serif;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	white-space: nowrap;
	flex-shrink: 0;

	&:hover {
		border-color: #3200C1;
		color: #3200C1;
	}
`

const ViewToggle = styled.div`
	display: flex;
	align-items: center;
	gap: 2px;
	background: rgb(234, 242, 252);
	border-radius: 6px;
	padding: 3px;
	height: 36px;
	flex-shrink: 0;
`

const ViewBtn = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 30px;
	height: 28px;
	border-radius: 4px;
	border: 0;
	cursor: pointer;
	font-family: inherit;
	transition: 120ms;
	background: transparent;
	color: rgb(50, 0, 193);

	&[data-active="true"] {
		background: rgb(50, 0, 193);
		color: rgb(255, 255, 255);
	}
`

const FilterSelect = styled.select`
	appearance: none;
	border: 1px solid #E0E0E0;
	border-radius: 8px;
	background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23333' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat right 10px center;
	padding: 8px 32px 8px 12px;
	font-family: nunito, sans-serif;
	font-size: 14px;
	font-weight: 600;
	color: #1a1a2e;
	cursor: pointer;
	white-space: nowrap;
	flex-shrink: 0;
	min-width: 120px;
	outline: none;

	&:focus {
		border-color: #3200C1;
	}
`

const SaveButton = styled.button`
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	background: transparent;
	border: 1px solid #E0E0E0;
	border-radius: 8px;
	padding: 0;
	height: 37px;
	min-width: 116px;
	cursor: pointer;
	perspective: 700px;
	white-space: nowrap;
	flex-shrink: 0;
	transition: border-color 0.2s;

	&:hover:not(:disabled) {
		border-color: #3200C1;
	}

	&:disabled {
		cursor: default;
	}

	&[data-status="saved"] {
		border-color: #3200C1;
	}
`

const SaveButtonInner = styled.span`
	position: relative;
	display: block;
	width: 100%;
	height: 100%;
	transform-style: preserve-3d;
	transition: transform 0.6s cubic-bezier(0.4, 0.2, 0.2, 1);

	[data-status="saved"] & {
		transform: rotateX(180deg);
	}
`

const SaveButtonFace = styled.span`
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	backface-visibility: hidden;
	border-radius: 7px;
	font-family: nunito, sans-serif;
	font-size: 14px;
	font-weight: 500;
	color: #333;
	white-space: nowrap;

	&.face-back {
		background: #3200C1;
		color: #fff;
		font-weight: 700;
		transform: rotateX(180deg);
	}
`
